'use client';

import { useState, useEffect, useRef } from 'react';

import { useI18n, useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import {
	Button,
	HorizontalLine,
	Icon,
	IconFsTree,
	IconHamburgerMenu,
	IconPreferredPartnerLogo,
	LanguageSelect,
	Link,
} from '@fshr/components';
import SignOutButton from './sign-out-button';

import MobileNavLinks from './mobile-nav-links';
import MobileProfileNavigation from './mobile-profile-navigation';
import type { NavigationLink } from '@micro-apps/env';
import { Empty } from '@fshr/microservices/compiled/profile.v1';
import PreferredPartnerMobileProfileNavigation from '@profiles/app/(preferredpartner)/_components/navigation/mobile-profile-navigation';

interface MobileNavigationProps {
	navLinks: NavigationLink[];
	isSignedIn?: boolean;
	isPersisted?: boolean;
	isFullyAuthenticated: boolean;
	avatar?: string;
	name?: string;
	initials?: string;
	advisorId?: string;
	hasUpcomingTrips?: boolean;
	hasMembership?: boolean;
	hasOwnedProperties?: boolean;
	isEmployee?: boolean;
	signOut: () => Promise<Empty>;
	topBarLinks?: NavigationLink[];
	ppMode?: boolean;
}

export default function MobileNavigation({
	navLinks,
	isSignedIn = false,
	isPersisted = false,
	avatar,
	name,
	initials,
	advisorId,
	hasUpcomingTrips,
	hasMembership,
	hasOwnedProperties,
	isEmployee,
	signOut,
	isFullyAuthenticated,
	topBarLinks,
	ppMode,
}: MobileNavigationProps) {
	const t = useTranslations();
	const { localeLink } = useI18n();

	const [isOpen, setIsOpen] = useState(false);
	const open = () => setIsOpen(true);
	const close = () => setIsOpen(false);

	const sidebarRef = useRef<HTMLDivElement>(null);
	const backdropRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const sidebarEl = sidebarRef.current;
		const backdropEl = backdropRef.current;

		const onTransitionEnd = (evt: TransitionEvent) => {
			if (evt.propertyName === 'translate' && !isOpen && backdropEl) {
				backdropEl.classList.add('bottom-full');
				backdropEl.classList.remove('bottom-0');
			}
		};

		const onOutsideClick = (evt: MouseEvent) => {
			const sidebarEl = sidebarRef.current;
			const backdropEl = backdropRef.current;

			// The clicked element is outside the modal and still in the DOM, and within the modal container
			if (
				sidebarEl &&
				backdropEl &&
				document.contains(evt.target as Node) &&
				!sidebarEl.contains(evt.target as Node) &&
				backdropEl.contains(evt.target as Node) &&
				close
			) {
				close();
			}
		};

		const onKeyDown = (evt: KeyboardEvent) => {
			if (isOpen && evt.key === 'Escape') {
				close();
			}
		};

		if (isOpen && backdropEl) {
			backdropEl.classList.remove('bottom-full', 'bg-transparent');
			backdropEl.classList.add('bottom-0', 'bg-black/50');
		}

		if (!isOpen && backdropEl) {
			backdropEl.classList.add('bg-transparent');
			backdropEl.classList.remove('bg-black/50');
		}

		document.body.classList.toggle('overflow-hidden', isOpen);
		document.addEventListener('click', onOutsideClick);
		document.addEventListener('keydown', onKeyDown);
		sidebarEl?.addEventListener('transitionend', onTransitionEnd);

		return () => {
			sidebarEl?.removeEventListener('transitionend', onTransitionEnd);
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('click', onOutsideClick);
			document.body.classList.remove('overflow-hidden');
		};
	}, [isOpen]);

	return (
		<>
			<Button intent="unstyled" onClick={() => open()} className="w-8 h-8">
				<IconHamburgerMenu fill={ppMode ? '#030303' : '#fff'} />
			</Button>

			<nav
				ref={backdropRef}
				className="fixed top-0 left-0 right-0 bottom-full bg-transparent flex flex-row justify-end z-50 transition-colors overflow-hidden"
			>
				<div
					ref={sidebarRef}
					className={cn(
						'max-w-[355px] w-full bg-white text-black overflow-y-auto transition-transform translate-x-full flex flex-col gap-[35px] py-250 z-50',
						{
							'translate-x-0': isOpen,
						}
					)}
				>
					<div
						className={cn(
							'flex flex-row items-center px-[35px]',
							ppMode && isSignedIn ? 'justify-end' : 'justify-between'
						)}
					>
						{!isSignedIn && (
							<Link
								className="text-caption2 capitalize flex items-center gap-[5px]"
								intent="unstyled"
								href={localeLink('/sign-in/')}
								onClick={close}
							>
								<Icon icon="person" className="text-[18px]" />
								{t('Sign in')}
							</Link>
						)}

						{!ppMode ? <LanguageSelect /> : null}

						<Button intent="unstyled" onClick={() => close()}>
							<Icon icon="close" className="text-[20px]" />
							<span className="visually-hidden">{t('Close navigation')}</span>
						</Button>
					</div>

					{ppMode ? (
						<div className="px-[35px]">
							<div className="inline-flex w-fit flex-col items-center gap-[8px]">
								<IconFsTree width={34} height={52} className="shrink-0" />
								<IconPreferredPartnerLogo width={164} height={29} className="shrink-0" />
							</div>
						</div>
					) : null}

					{isSignedIn &&
						(ppMode ? (
							<PreferredPartnerMobileProfileNavigation
								isFullyAuthenticated={isFullyAuthenticated}
								avatar={avatar}
								name={name}
								initials={initials}
								advisorId={advisorId}
								close={close}
							/>
						) : (
							<MobileProfileNavigation
								isPersisted={isPersisted}
								isFullyAuthenticated={isFullyAuthenticated}
								avatar={avatar}
								name={name}
								initials={initials}
								hasUpcomingTrips={hasUpcomingTrips}
								hasMembership={hasMembership}
								hasOwnedProperties={hasOwnedProperties}
								isEmployee={isEmployee}
								close={close}
							/>
						))}

					{!ppMode ? (
						<div className="px-[35px]">
							<Link
								href="/"
								intent="unstyled"
								className="grid place-content-center leading-none text-[40px] h-[40px] w-[26px]"
								prefetch={false}
							>
								<Icon icon="tree" />
								<span className="visually-hidden">{t('Go to the Four Seasons home page')}</span>
							</Link>
						</div>
					) : null}

					{ppMode && topBarLinks?.length ? (
						<MobileNavLinks
							links={topBarLinks ?? []}
							isFullyAuthenticated={true}
							className="-mb-[35px]"
							close={close}
						/>
					) : null}

					{ppMode && topBarLinks?.length ? (
						<div className="px-[35px]">
							<HorizontalLine />
						</div>
					) : null}

					<MobileNavLinks
						links={navLinks}
						isFullyAuthenticated={isFullyAuthenticated}
						className="-mb-[35px]"
						close={close}
					/>

					{isSignedIn && (
						<div className="border-t border-neutral-300 mx-[35px] flex flex-col gap-[35px] py-300 items-start">
							<SignOutButton size="medium" onSignOut={close} signOut={signOut} />
						</div>
					)}
				</div>
			</nav>
		</>
	);
}
